/**
 * Engagement decorations — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_decor_ideas`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const engagementDecorationsPack: ToilePack = {
    slug: 'engagement-decorations',
    category: 'occasions',
    name: 'Engagement decorations',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'rings', label: 'Ring backdrop', subject: 'a flower wall with two large gold rings and roses'},
        {key: 'arch', label: 'Floral arch', subject: 'a floral arch with white roses'},
        {key: 'neon', label: 'Neon sign', subject: 'a greenery wall with a pink neon sign reading "Engaged"'},
    ],
    styles: [
        {key: 'home', label: 'At home', style: 'set in a family living room with the furniture moved back and a low table of sweets in front, an eye-level view', palette: 'flower colours with warm wood and cream walls', framing: 'the setup centered, the table in front'},
        {key: 'hall', label: 'Banquet hall', style: 'set on a low platform in a banquet hall with two chairs and draped ceiling fabric, a wide view from the floor', palette: 'ivory, blush and gold', framing: 'the setup filling the middle of the frame'},
        {key: 'rooftop', label: 'Rooftop', style: 'set on a rooftop at dusk with fairy lights strung overhead and city lights soft behind, a three-quarter view', palette: 'flower colours against a deep blue dusk', framing: 'the setup in the lower two thirds, the sky above'},
    ],
    directions: ['pastel pink', 'roka ceremony', 'ring platter'],
    directionHint: 'try: pastel pink',
    seo: {
        title: 'Engagement decoration: rings, floral arch, neon sign',
        metaDescription: 'Nine engagement decoration pictures: a ring backdrop, a floral arch and a neon sign, set at home, in a banquet hall and on a rooftop.',
        h1: 'Engagement decoration: ring backdrop, floral arch, neon sign',
        tagline: 'Nine pictures, three engagement pieces, each set at home, in a banquet hall and on a rooftop.',
        intro: 'Engagement decoration for a ring ceremony: a flower wall with two large gold rings and roses, a floral arch of white roses, and a greenery wall with a pink neon sign reading Engaged. Each piece is set in a family living room with the furniture moved back, on a low platform in a banquet hall, or on a rooftop under fairy lights at dusk. Type a twist, such as pastel pink or roka ceremony, and the same setups carry it.',
        useCases: [
            'a ring ceremony at home',
            'an engagement stage in a banquet hall',
            'a rooftop evening party',
            'a backdrop for engagement photos',
        ],
        faq: [
            {q: 'What are the three pieces?', a: 'A ring backdrop with two large gold rings and roses, a floral arch of white roses, and a greenery wall with a pink neon sign reading Engaged.'},
            {q: 'Where are they set?', a: 'In a family living room, in a banquet hall, and on a rooftop at dusk.'},
            {q: 'Does the sign say anything?', a: 'Yes, it reads Engaged. The other pictures have no text.'},
            {q: 'Can I ask for a different colour?', a: 'Yes. Type a twist such as pastel pink, and the same three pieces are shown in that colour.'},
        ],
    },
}
